import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Wand2, Image, Type, Palette, Music, Upload, Download,
  Crop, RotateCw, Sun, Contrast, SlidersHorizontal, Layers, 
  Plus, X, Check, RefreshCw, Share2, Save, Trash2, ZoomIn, ZoomOut,
  Brush, Eraser, Square, Circle, Triangle, Star, Heart, Smile,
  ChevronLeft, ChevronRight, Grid, Loader2, ImagePlus, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CreativityZone = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ai-art');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // AI Art State
  const [artStyles, setArtStyles] = useState([]);
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('gemini');
  const [artPrompt, setArtPrompt] = useState('');
  const [generatingArt, setGeneratingArt] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [myGenerations, setMyGenerations] = useState([]);
  
  // Image Editor State
  const [editImage, setEditImage] = useState(null);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [rotation, setRotation] = useState(0);
  
  // Text & Graphics State
  const [textStyles, setTextStyles] = useState([]);
  const [stickerPacks, setStickerPacks] = useState([]);
  const [textOverlays, setTextOverlays] = useState([]);
  const [selectedStickers, setSelectedStickers] = useState([]);
  
  // Inspiration Feed
  const [inspirationPosts, setInspirationPosts] = useState([]);
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('socyy_token');

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      
      const [stylesRes, providersRes, filtersRes, textRes, stickersRes, inspirationRes] = await Promise.all([
        fetch(`${API}/api/creativity/ai-art/styles`),
        fetch(`${API}/api/creativity/ai-providers`),
        fetch(`${API}/api/creativity/filters`),
        fetch(`${API}/api/creativity/text-styles`),
        fetch(`${API}/api/creativity/stickers`),
        fetch(`${API}/api/creativity/inspiration`, { headers })
      ]);
      
      const [stylesData, providersData, filtersData, textData, stickersData, inspirationData] = await Promise.all([
        stylesRes.json(), providersRes.json(), filtersRes.json(), textRes.json(), stickersRes.json(), inspirationRes.json()
      ]);
      
      setArtStyles(stylesData.styles || []);
      setAiProviders(providersData.providers || []);
      setFilters(filtersData.filters || []);
      setTextStyles(textData.styles || []);
      setStickerPacks(stickersData.packs || []);
      setInspirationPosts(inspirationData.posts || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, []);

  const fetchMyGenerations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/creativity/ai-art/my-generations?limit=12`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setMyGenerations(data.generations || []);
    } catch (err) {
      console.error('Error fetching generations:', err);
    }
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('socyy_user');
    if (userData) setUser(JSON.parse(userData));
    fetchData();
    fetchMyGenerations();
  }, [fetchData, fetchMyGenerations]);

  // AI Art Generation
  const generateArt = async () => {
    if (!artPrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    
    setGeneratingArt(true);
    setGeneratedImages([]);
    
    try {
      const res = await fetch(`${API}/api/creativity/ai-art/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          prompt: artPrompt,
          provider: selectedProvider,
          style: selectedStyle,
          quality: 'medium',
          num_images: 1
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setGeneratedImages(data.images || []);
        toast.success('Art generated successfully!');
        fetchMyGenerations();
      } else {
        toast.error(data.detail || 'Generation failed');
      }
    } catch (err) {
      toast.error('Error generating art');
    }
    
    setGeneratingArt(false);
  };

  const shareArt = async (imageId) => {
    try {
      const res = await fetch(`${API}/api/creativity/ai-art/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ image_id: imageId })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Shared to your feed!');
      }
    } catch (err) {
      toast.error('Error sharing');
    }
  };

  // Image Upload Handler
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setEditImage(event.target?.result);
        setActiveTab('editor');
      };
      reader.readAsDataURL(file);
    }
  };

  // Get combined filter style
  const getFilterStyle = () => {
    const filterCss = filters.find(f => f.id === selectedFilter)?.css || '';
    return `${filterCss} brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
  };

  // AI Art Tab
  const AIArtTab = () => (
    <div className="space-y-6">
      {/* Prompt Input */}
      <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-800/50 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-purple-400" /> AI Art Generator
        </h3>
        
        <textarea
          value={artPrompt}
          onChange={(e) => setArtPrompt(e.target.value)}
          placeholder="Describe the image you want to create... (e.g., 'A futuristic city at sunset with flying cars')"
          className="w-full bg-zinc-900/50 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-500 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          rows={3}
          data-testid="art-prompt-input"
        />
        
        {/* Provider Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-zinc-300 mb-2">AI Provider</label>
          <div className="flex gap-3">
            {aiProviders.map(provider => (
              <button
                key={provider.id}
                onClick={() => setSelectedProvider(provider.id)}
                className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                  selectedProvider === provider.id
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <p className="font-medium">{provider.name}</p>
                <p className="text-xs opacity-70">{provider.model}</p>
              </button>
            ))}
          </div>
        </div>
        
        {/* Style Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-zinc-300 mb-2">Art Style (Optional)</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStyle(null)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                !selectedStyle ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              None
            </button>
            {artStyles.map(style => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center gap-1 ${
                  selectedStyle === style.id ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                <span>{style.icon}</span> {style.name}
              </button>
            ))}
          </div>
        </div>
        
        <button
          onClick={generateArt}
          disabled={generatingArt || !artPrompt.trim()}
          className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="generate-art-btn"
        >
          {generatingArt ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" /> Generate Art
            </>
          )}
        </button>
      </div>

      {/* Generated Images */}
      {generatedImages.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Generated Art</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedImages.map((img, idx) => (
              <div key={idx} className="relative group">
                <img 
                  src={img.data_url} 
                  alt="Generated art" 
                  className="w-full rounded-xl"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                  <button
                    onClick={() => {
                      setEditImage(img.data_url);
                      setActiveTab('editor');
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <Brush className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => shareArt(img.image_id)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                  <a
                    href={img.data_url}
                    download="ai-art.png"
                    className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <Download className="w-4 h-4" /> Save
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Generations */}
      {myGenerations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Recent Generations</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {myGenerations.map(gen => (
              <div key={gen.image_id} className="aspect-square bg-zinc-800 rounded-lg overflow-hidden group relative cursor-pointer">
                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                  <ImagePlus className="w-8 h-8" />
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <p className="text-xs text-white text-center px-2 line-clamp-3">{gen.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Image Editor Tab
  const EditorTab = () => (
    <div className="space-y-6">
      {/* Upload Section */}
      {!editImage ? (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-zinc-700 rounded-2xl p-12 text-center cursor-pointer hover:border-purple-500 transition-colors"
        >
          <Upload className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-white mb-2">Upload an image to edit</p>
          <p className="text-sm text-zinc-400">Click or drag and drop</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="sr-only"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Canvas Area */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
              <div className="relative overflow-hidden rounded-xl bg-zinc-950" style={{ transform: `rotate(${rotation}deg)` }}>
                <img 
                  src={editImage} 
                  alt="Edit canvas"
                  className="w-full"
                  style={{ filter: getFilterStyle() }}
                />
                
                {/* Text Overlays */}
                {textOverlays.map((text, idx) => (
                  <div
                    key={idx}
                    className="absolute text-white text-2xl font-bold"
                    style={{
                      left: text.x,
                      top: text.y,
                      fontFamily: text.fontFamily,
                      color: text.color
                    }}
                  >
                    {text.content}
                  </div>
                ))}
                
                {/* Stickers */}
                {selectedStickers.map((sticker, idx) => (
                  <div
                    key={idx}
                    className="absolute text-4xl cursor-move"
                    style={{ left: sticker.x, top: sticker.y }}
                  >
                    {sticker.emoji}
                  </div>
                ))}
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => setEditImage(null)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg flex items-center gap-2"
                >
                  <X className="w-4 h-4" /> Clear
                </button>
                <div className="flex gap-2">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2">
                    <Save className="w-4 h-4" /> Save
                  </button>
                  <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-2">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Panel */}
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" /> Filters
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {filters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedFilter(filter.id)}
                    className={`px-2 py-2 rounded-lg text-xs transition-all ${
                      selectedFilter === filter.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {filter.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Adjustments */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" /> Adjustments
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-zinc-400 flex items-center justify-between">
                    <span className="flex items-center gap-1"><Sun className="w-3 h-3" /> Brightness</span>
                    <span>{brightness}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 flex items-center justify-between">
                    <span className="flex items-center gap-1"><Contrast className="w-3 h-3" /> Contrast</span>
                    <span>{contrast}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={contrast}
                    onChange={(e) => setContrast(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 flex items-center justify-between">
                    <span className="flex items-center gap-1"><Palette className="w-3 h-3" /> Saturation</span>
                    <span>{saturation}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={saturation}
                    onChange={(e) => setSaturation(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 flex items-center justify-between">
                    <span className="flex items-center gap-1"><RotateCw className="w-3 h-3" /> Rotation</span>
                    <span>{rotation}°</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={rotation}
                    onChange={(e) => setRotation(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  setBrightness(100);
                  setContrast(100);
                  setSaturation(100);
                  setRotation(0);
                  setSelectedFilter('none');
                }}
                className="mt-3 w-full px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Text & Graphics Tab
  const TextGraphicsTab = () => (
    <div className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Type className="w-5 h-5" /> Add Text
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          {textStyles.map(style => (
            <button
              key={style.id}
              className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm"
              style={{ fontFamily: style.fontFamily, fontWeight: style.fontWeight }}
            >
              {style.name}
            </button>
          ))}
        </div>
        
        <input
          type="text"
          placeholder="Enter your text..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
        />
      </div>

      {/* Sticker Packs */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Smile className="w-5 h-5" /> Stickers
        </h3>
        
        {stickerPacks.map(pack => (
          <div key={pack.pack_id} className="mb-4">
            <h4 className="text-sm font-medium text-zinc-400 mb-2">{pack.name}</h4>
            <div className="flex flex-wrap gap-2">
              {pack.stickers.map((sticker, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedStickers([...selectedStickers, { emoji: sticker, x: '50%', y: '50%' }])}
                  className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center justify-center text-xl"
                >
                  {sticker}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Inspiration Tab
  const InspirationTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Lightbulb className="w-6 h-6 text-yellow-500" /> Inspiration Feed
      </h2>
      
      {inspirationPosts.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Grid className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No creations yet. Be the first!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {inspirationPosts.map(post => (
            <div key={post.post_id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group">
              {post.media?.[0] && (
                <div className="aspect-square bg-zinc-800">
                  <img 
                    src={post.media[0].url || post.media[0]} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-zinc-700 rounded-full">
                    {post.user?.avatar && <img src={post.user.avatar} alt="" className="w-full h-full rounded-full" />}
                  </div>
                  <span className="text-sm text-zinc-300">{post.user?.username || 'Anonymous'}</span>
                </div>
                <p className="text-xs text-zinc-500 line-clamp-2">{post.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // AI Art Remix Tab
  const RemixTab = () => {
    const [remixStyles, setRemixStyles] = useState([]);
    const [selectedRemixStyle, setSelectedRemixStyle] = useState(null);
    const [remixImage, setRemixImage] = useState(null);
    const [remixing, setRemixing] = useState(false);
    const [remixResult, setRemixResult] = useState(null);
    const remixInputRef = useRef(null);

    useEffect(() => {
      const fetchStyles = async () => {
        try {
          const res = await fetch(`${API}/api/creativity/remix/styles`);
          const data = await res.json();
          setRemixStyles(data.styles || []);
        } catch (err) {
          console.error('Error fetching remix styles:', err);
        }
      };
      fetchStyles();
    }, []);

    const handleRemixUpload = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setRemixImage(event.target?.result);
          setRemixResult(null);
        };
        reader.readAsDataURL(file);
      }
    };

    const performRemix = async () => {
      if (!remixImage || !selectedRemixStyle) {
        toast.error('Please upload an image and select a style');
        return;
      }

      setRemixing(true);
      try {
        const res = await fetch(`${API}/api/creativity/remix`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            image_data: remixImage,
            style_id: selectedRemixStyle,
            provider: selectedProvider
          })
        });

        const data = await res.json();
        if (data.success) {
          setRemixResult(data.images?.[0]);
          toast.success('Image remixed successfully!');
        } else {
          toast.error(data.detail || 'Remix failed');
        }
      } catch (err) {
        toast.error('Error remixing image');
      }
      setRemixing(false);
    };

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 border border-pink-800/50 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-pink-400" /> AI Art Remix
          </h3>
          <p className="text-zinc-400 text-sm mb-4">Upload your image and transform it into different artistic styles using AI</p>

          {/* Upload Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Upload Image</label>
              {!remixImage ? (
                <div 
                  onClick={() => remixInputRef.current?.click()}
                  className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center cursor-pointer hover:border-pink-500 transition-colors"
                >
                  <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
                  <p className="text-sm text-zinc-400">Click to upload image</p>
                  <input
                    ref={remixInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleRemixUpload}
                    className="sr-only"
                  />
                </div>
              ) : (
                <div className="relative">
                  <img src={remixImage} alt="To remix" className="w-full rounded-xl" />
                  <button 
                    onClick={() => { setRemixImage(null); setRemixResult(null); }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Result */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Result</label>
              {remixResult ? (
                <div className="relative">
                  <img src={remixResult.data_url} alt="Remixed" className="w-full rounded-xl" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                    <a
                      href={remixResult.data_url}
                      download="remixed-art.png"
                      className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" /> Save
                    </a>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center h-full flex items-center justify-center">
                  <p className="text-sm text-zinc-500">Remixed image will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Style Selection */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-zinc-300 mb-2">Select Style</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {remixStyles.map(style => (
                <button
                  key={style.id}
                  onClick={() => setSelectedRemixStyle(style.id)}
                  className={`px-3 py-2 rounded-lg text-sm transition-all flex flex-col items-center gap-1 ${
                    selectedRemixStyle === style.id
                      ? 'bg-pink-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <span className="text-xl">{style.icon}</span>
                  <span className="text-xs">{style.name}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={performRemix}
            disabled={remixing || !remixImage || !selectedRemixStyle}
            className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="remix-art-btn"
          >
            {remixing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Remixing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" /> Remix Image
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Meme Maker Tab
  const MemeMakerTab = () => {
    const [memeImage, setMemeImage] = useState(null);
    const [topText, setTopText] = useState('');
    const [bottomText, setBottomText] = useState('');
    const [fontSize, setFontSize] = useState(48);
    const [fontFamily, setFontFamily] = useState('Impact, Haettenschweiler, sans-serif');
    const [textColor, setTextColor] = useState('#ffffff');
    const [strokeColor, setStrokeColor] = useState('#000000');
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [caption, setCaption] = useState('');
    const [sharing, setSharing] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [fonts, setFonts] = useState([]);
    const [selectedBg, setSelectedBg] = useState(null);
    const memeCanvasRef = useRef(null);
    const memeFileRef = useRef(null);

    useEffect(() => {
      const fetchTemplates = async () => {
        try {
          const res = await fetch(`${API}/api/creativity/meme-maker/templates`);
          const data = await res.json();
          setTemplates(data.templates || []);
          setFonts(data.fonts || []);
        } catch (err) {
          console.error('Error fetching meme templates');
        }
      };
      fetchTemplates();
    }, []);

    const handleMemeUpload = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setMemeImage(event.target?.result);
          setSelectedBg(null);
        };
        reader.readAsDataURL(file);
      }
    };

    const selectTemplate = (template) => {
      setSelectedBg(template);
      setMemeImage(null);
    };

    const drawMeme = useCallback(() => {
      const canvas = memeCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      const drawContent = () => {
        // Draw text helper
        const drawText = (text, y, align) => {
          if (!text) return;
          ctx.font = `bold ${fontSize}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.fillStyle = textColor;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          ctx.lineJoin = 'round';

          const lines = [];
          const words = text.split(' ');
          let line = '';
          const maxWidth = canvas.width - 40;
          for (const word of words) {
            const test = line + word + ' ';
            if (ctx.measureText(test).width > maxWidth && line) {
              lines.push(line.trim());
              line = word + ' ';
            } else {
              line = test;
            }
          }
          lines.push(line.trim());

          const lineHeight = fontSize * 1.2;
          let startY = align === 'top' ? fontSize + 10 : canvas.height - (lines.length * lineHeight) - 10 + fontSize;

          lines.forEach((l, i) => {
            const ly = startY + i * lineHeight;
            ctx.strokeText(l, canvas.width / 2, ly);
            ctx.fillText(l, canvas.width / 2, ly);
          });
        };

        drawText(topText.toUpperCase(), 0, 'top');
        drawText(bottomText.toUpperCase(), 0, 'bottom');
      };

      if (memeImage) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          drawContent();
        };
        img.src = memeImage;
      } else if (selectedBg) {
        canvas.width = selectedBg.width;
        canvas.height = selectedBg.height;
        ctx.fillStyle = selectedBg.color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (selectedBg.panels === 2) {
          ctx.strokeStyle = '#ccc';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, canvas.height / 2);
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        } else if (selectedBg.panels === 4) {
          ctx.strokeStyle = '#ccc';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(canvas.width / 2, 0);
          ctx.lineTo(canvas.width / 2, canvas.height);
          ctx.moveTo(0, canvas.height / 2);
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        }
        drawContent();
      } else {
        canvas.width = 800;
        canvas.height = 600;
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, 800, 600);
        ctx.fillStyle = '#444';
        ctx.font = '20px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('Upload an image or pick a template', 400, 300);
      }
    }, [memeImage, selectedBg, topText, bottomText, fontSize, fontFamily, textColor, strokeColor, strokeWidth]);

    useEffect(() => { drawMeme(); }, [drawMeme]);

    const downloadMeme = () => {
      const canvas = memeCanvasRef.current;
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = 'socyy-meme.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    const shareMeme = async () => {
      const canvas = memeCanvasRef.current;
      if (!canvas) return;
      if (!memeImage && !selectedBg) {
        toast.error('Create a meme first!');
        return;
      }
      setSharing(true);
      try {
        const imageData = canvas.toDataURL('image/png');
        const res = await fetch(`${API}/api/creativity/meme-maker/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            image_data: imageData,
            caption: caption,
            auto_hashtag: true
          })
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Meme shared to feed with #memegallery!');
          setCaption('');
        } else {
          toast.error(data.detail || 'Failed to share');
        }
      } catch (err) {
        toast.error('Error sharing meme');
      }
      setSharing(false);
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Canvas Preview */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Smile className="w-5 h-5 text-green-400" /> Meme Preview
              </h3>
              <div className="bg-zinc-950 rounded-xl overflow-hidden flex items-center justify-center" style={{minHeight: 300}}>
                <canvas
                  ref={memeCanvasRef}
                  className="max-w-full max-h-[500px] object-contain"
                  data-testid="meme-canvas"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={() => memeFileRef.current?.click()}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg flex items-center gap-2 text-sm"
                  data-testid="meme-upload-btn"
                >
                  <Upload className="w-4 h-4" /> Upload Image
                </button>
                <input
                  ref={memeFileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleMemeUpload}
                  className="sr-only"
                />
                <button
                  onClick={downloadMeme}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 text-sm"
                  data-testid="meme-download-btn"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
                <button
                  onClick={shareMeme}
                  disabled={sharing}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                  data-testid="meme-share-btn"
                >
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Share to #MemeGallery
                </button>
              </div>

              {/* Caption for sharing */}
              <div className="mt-3">
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption (optional)..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm placeholder-zinc-500"
                  data-testid="meme-caption-input"
                />
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {/* Text Inputs */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Type className="w-4 h-4" /> Meme Text
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Top Text</label>
                  <input
                    type="text"
                    value={topText}
                    onChange={(e) => setTopText(e.target.value)}
                    placeholder="TOP TEXT..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm uppercase placeholder-zinc-500"
                    data-testid="meme-top-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Bottom Text</label>
                  <input
                    type="text"
                    value={bottomText}
                    onChange={(e) => setBottomText(e.target.value)}
                    placeholder="BOTTOM TEXT..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm uppercase placeholder-zinc-500"
                    data-testid="meme-bottom-text"
                  />
                </div>
              </div>
            </div>

            {/* Font & Style */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" /> Style
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Font</label>
                  <div className="grid grid-cols-2 gap-2">
                    {fonts.map(f => (
                      <button
                        key={f.id}
                        onClick={() => setFontFamily(f.family)}
                        className={`px-2 py-1.5 rounded-lg text-xs transition-all ${
                          fontFamily === f.family ? 'bg-green-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        style={{ fontFamily: f.family }}
                        data-testid={`meme-font-${f.id}`}
                      >
                        {f.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 flex justify-between mb-1">
                    <span>Font Size</span><span>{fontSize}px</span>
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="96"
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                    data-testid="meme-font-size"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 flex justify-between mb-1">
                    <span>Stroke Width</span><span>{strokeWidth}px</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="8"
                    value={strokeWidth}
                    onChange={(e) => setStrokeWidth(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                    data-testid="meme-stroke-width"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-zinc-400 mb-1 block">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent"
                        data-testid="meme-text-color"
                      />
                      <span className="text-xs text-zinc-400">{textColor}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-zinc-400 mb-1 block">Stroke Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={strokeColor}
                        onChange={(e) => setStrokeColor(e.target.value)}
                        className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent"
                        data-testid="meme-stroke-color"
                      />
                      <span className="text-xs text-zinc-400">{strokeColor}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Templates */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Templates
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => selectTemplate(t)}
                    className={`aspect-square rounded-lg border-2 flex items-center justify-center text-xs font-medium transition-all ${
                      selectedBg?.id === t.id
                        ? 'border-green-500 ring-2 ring-green-500/30'
                        : 'border-zinc-700 hover:border-zinc-500'
                    }`}
                    style={{ backgroundColor: t.color === '#ffffff' ? '#e5e7eb' : t.color }}
                    data-testid={`meme-template-${t.id}`}
                  >
                    <span className={t.color === '#000000' ? 'text-white' : 'text-zinc-600'}>
                      {t.panels ? `${t.panels}P` : t.name.split(' ')[1]?.[0] || t.name[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'ai-art', label: 'AI Art', icon: Wand2 },
    { id: 'remix', label: 'AI Remix', icon: RefreshCw },
    { id: 'meme-maker', label: 'Meme Maker', icon: Smile },
    { id: 'editor', label: 'Image Editor', icon: Image },
    { id: 'text-graphics', label: 'Text & Graphics', icon: Type },
    { id: 'inspiration', label: 'Inspiration', icon: Lightbulb }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="creativity-zone-page">
      {/* Header */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/feed')} className="text-zinc-400 hover:text-white">←</button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <h1 className="text-xl font-bold text-white">Creativity Zone</h1>
            </div>
          </div>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="sr-only"
          />
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'ai-art' && AIArtTab()}
        {activeTab === 'remix' && RemixTab()}
        {activeTab === 'meme-maker' && MemeMakerTab()}
        {activeTab === 'editor' && EditorTab()}
        {activeTab === 'text-graphics' && TextGraphicsTab()}
        {activeTab === 'inspiration' && InspirationTab()}
      </main>
    </div>
  );
};

export default CreativityZone;
